/* global ace */

import React from 'react';
import PropTypes from 'prop-types';
import scriptjs from 'scriptjs';
import Modal from 'react-modal';

import stringUtils from '../../../../../qti/utils/StringUtils';
import constants from '../../../../../qti/Constants';
import sessionReader from '../../../../../store/readHelpers/SessionReader';
import CodeCompilerService from '../../../../../services/questionView/CodeCompilerService';
import { t } from '../../../../../services/i18nService';

import ExtendedTextCodeCompilerModal from './ExtendedTextCodeCompilerModal';
import ExtendedTextCodeCompilerSamples from './ExtendedTextCodeCompilerTestCases';
import OutcomeIcon from './../../../interactionOutcome/OutcomeIcon';
import styles from './codeWrapper.scss';
import { reportError } from '../../../../../services/ErrorReporting';
import uiService from '../../../../../services/UIService';
import extendedTextCodeA11ySelector from '../../../../../selectors/question/interactions/extendedTextInteraction/code/ExtendedTextCodeA11ySelector';


const aceEditorUrl = `${constants.staticLibPaths.ACEEDITOR}ace.js`;

class ExtendedTextCodeInteraction extends React.Component {
    static propTypes = {
        autoSave: PropTypes.func.isRequired,
        codeMode: PropTypes.string,
        compilationMode: PropTypes.bool,
        correctResponses: PropTypes.array, // The previously stored data
        defaultValue: PropTypes.array,
        extraResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired,
        questionModel: PropTypes.object,
        services: PropTypes.object,
        shouldSaveOnEmptyChange: PropTypes.bool.isRequired,
        showGutter: PropTypes.bool,
        userData: PropTypes.string, // The previously stored data
    };

    constructor(props) {
        super(props);
        const { model, correctResponses = [] } = props;
        const parsedUserResponse = this.parseUserResponse();
        this.state = {
            editorInstanceName: `ace_${stringUtils.getUID()}`,
            outputRegionId: `code-output-${stringUtils.getUID()}`,
            error: {},
            evalTestCases: correctResponses,
            evalTestCasesResults: parsedUserResponse.evalTestCasesResults,
            globalCompilationError: parsedUserResponse.globalCompilationError,
            isEmpty: !props.userData || props.userData.length === 0,
            mode: 'compiling',
            runTimeInfo: {},
            sampleTestCases: model.codeCompilerSamples,
            sampleTestCasesResults: [],
            showCodeCompilerModal: false,
        };
        this.isA11yV5AndAboveSupported = extendedTextCodeA11ySelector.isA11yV5AndAboveSupported();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        Modal.setAppElement('body');
    }

    componentDidMount() {
        const instanceName = this.state.editorInstanceName;
        scriptjs(aceEditorUrl, () => {
            if (!document.getElementById(instanceName)) {
                // might have navigated away from the page before the script loaded
                return;
            }
            if (!window.ace) {
                scriptjs.get(aceEditorUrl, () => {
                    if (!window.ace) {
                        return;
                    }
                    window.console.log('Second ACEEDITOR load try');
                    this.loadAceEditor(instanceName);
                });
                return;
            }
            this.loadAceEditor(instanceName);
        });
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { services, questionModel } = this.props;

        if (this.isEditorInitialized() && nextProps.isReadOnly !== this.props.isReadOnly) {
            try {
                if (services.questionSelectionService.isQuestionInSectionWithSelectionEnabled(questionModel.contentRevisionId)) {
                    this.editor.setOptions({
                        readOnly: nextProps.isReadOnly,
                    });
                }
            } catch (e) {
                reportError(`Error toggling ACE editor readOnly state. ${questionModel.contentRevisionId}`);
            }
        }
    }

    componentWillUnmount() {
        if (this.focusOnEnterKeydown && this.aceEditorWrapper) {
            this.aceEditorWrapper.removeEventListener('keydown', this.focusOnEnterKeydown);
        }
        if (this.liveRegionClearTimeoutId) {
            clearTimeout(this.liveRegionClearTimeoutId);
            this.liveRegionClearTimeoutId = null;
        }
        if (this.editor) {
            this.editor.destroy();
        }
    }

    /**
     * Resolves the evaluated testcases responses and also the report for a global compilation error, if it occurred.
     * See 2.a. in SPEC https://inspera.atlassian.net/wiki/spaces/ITP/pages/233439236/CheckSourceCodeCompilation
     * @returns
     */
    parseUserResponse = () => {
        const { extraResponses = [] } = this.props;
        const shouldParseExtraResponses = sessionReader.isContextViewDelivery() || sessionReader.isContextViewGradingQuestionDelivery();
        if (shouldParseExtraResponses &&
            extraResponses.length > 0 &&
            'RESPONSE_compiler_response' in extraResponses[0] &&
            extraResponses[0].RESPONSE_compiler_response.length > 0
        ) {
            const parsedCompilerResponse = JSON.parse(extraResponses[0].RESPONSE_compiler_response);
            // Global compilation error
            if (parsedCompilerResponse.status_code === 11) {
                return {
                    evalTestCasesResults: parsedCompilerResponse.testcases,
                    globalCompilationError: parsedCompilerResponse.runtime_info.cmperr,
                };
            }
            return { evalTestCasesResults: parsedCompilerResponse.testcases };
        }
        return { evalTestCasesResults: [] };
    };

    /**
     * Moved focus to the focusable element after or before the ace editor instance based on direction.
     */
    focusElement = (direction = 'next') => {
        const focusables = Array.from(document.querySelectorAll('input, button, select, a, textarea, *[tabindex]'));

        if (focusables.length <= 1) return;

        // Find the index of the current Ace text input
        const aceIndex = focusables.findIndex(el =>
            el.className === 'ace_text-input' &&
            el.parentElement.id === this.state.editorInstanceName
        );

        if (aceIndex === -1) return;

        let targetIndex;
        if (direction === 'previous') {
        // Go back one, or wrap to the end if you're at the start
            targetIndex = aceIndex - 1;
            if (targetIndex < 0) targetIndex = focusables.length - 1;
        } else {
        // Go forward one, or wrap to the start if you're at the end
            targetIndex = aceIndex + 1;
            if (targetIndex >= focusables.length) targetIndex = 0;
        }

        const elementToFocus = focusables[targetIndex];
        if (elementToFocus) {
            elementToFocus.focus();
        }
    };

    // Map the old names to the new helper for compatibility
    focusNext = () => this.focusElement('next');
    focusPrevious = () => this.focusElement('previous');


    loadAceEditor = (instanceName) => {
        const { codeMode, isReadOnly = false, showGutter, userData, defaultValue, shouldSaveOnEmptyChange } = this.props;
        const theme = uiService.shouldUseDarkMode() ? 'merbivore' : 'eclipse';
        // This is to clear the hidden announcement text after announcement
        const liveRegionClearDelayMs = 1800;
        const maxLines = !sessionReader.isViewMediaScreen()
            && !sessionReader.isContextPreview() ? Infinity : undefined;

        this.editor = ace.edit(instanceName);
        this.editor.setOptions({
            readOnly: isReadOnly,
            showGutter,
            theme: `ace/theme/${theme}`,
            maxLines,
        });
        this.editor.getSession().setUseWrapMode(true);
        if (codeMode) {
            this.editor.session.setMode(`ace/mode/${codeMode}`);
        }
        if (userData) {
            this.editor.setValue(userData, 1);
            if (sessionReader.isViewMediaPrint()) {
                const lines = this.editor.session.getLength();
                const paddingLines = Math.ceil(lines / 200) + 1;
                this.editor.session.insert({ row: lines, column: 0 }, '\n'.repeat(paddingLines));
            }
        } else if (!sessionReader.isContextAnyViewDeliveryType() && defaultValue.length) {
            this.editor.setValue(defaultValue[0], 1);
            if (sessionReader.isViewMediaPrint()) {
                const lines = this.editor.session.getLength();
                const paddingLines = Math.ceil(lines / 200) + 1;
                this.editor.session.insert({ row: lines, column: 0 }, '\n'.repeat(paddingLines));
            }
        }

        // This is to prevent the user from undoing back to the empty state before content was inserted and possibly loose everything.
        this.editor.getSession().getUndoManager().reset();

        if (shouldSaveOnEmptyChange) {
            this.editor.on('change', () => {
                if (!this.isEditorInitialized()) {
                    return;
                }

                const isEditorEmpty = this.isEditorDataEmpty();
                if (this.state.isEmpty !== isEditorEmpty) {
                    this.setState({
                        isEmpty: isEditorEmpty,
                    });
                    this.props.autoSave();
                }
            });
        }

        // Add keyboard commands for accessibility
        if (this.isA11yV5AndAboveSupported) {
            const textInput = this.editor.textInput.getElement();
            const container = this.aceEditorWrapper;

            // Initial state: not editing, focusable container
            textInput.setAttribute('tabindex', -1);
            container.setAttribute('tabindex', 0);
            container.setAttribute('aria-label', t('extendedTextCode.editorAriaLabel'));

            // Focus on Enter keydown
            const focusOnEnterKeydown = (e) => {
                if (e.target === container && e.keyCode === 13) { // Enter key
                    e.stopPropagation();
                    e.preventDefault();
                    this.editor.focus();
                }
            };
            container.addEventListener('keydown', focusOnEnterKeydown);

            // Clean up listener on unmount
            this.focusOnEnterKeydown = focusOnEnterKeydown; // Save reference for cleanup

            // Command to exit editing mode
            this.editor.commands.addCommand({
                name: 'blurTextInput',
                bindKey: 'Esc',
                exec: (editor) => {
                    editor.blur(); // Blurs the textInput
                    container.focus(); // Focus the container
                },
                readOnly: true,
            });
        } else {
            // Legacy behavior for older environments
            this.editor.commands.addCommand({
                name: 'blur editor forward',
                exec: () => this.focusNext(),
                bindKey: { mac: 'esc', win: 'esc' },
            });
        }

        this.editor.commands.addCommand({
            name: 'announceSelectionOnCancel',
            bindKey: { win: 'Down|Up|Left|Right', mac: 'Down|Up|Left|Right' },
            exec: (editor) => {
                if (!this.isA11yV5AndAboveSupported) {
                    return false;
                }

                const selectedCode = editor.getSelectedText();
                if (selectedCode && selectedCode.length > 0) {
                    const liveRegion = document.getElementById(`${this.state.editorInstanceName}_live_region`);
                    if (liveRegion) {
                        liveRegion.innerText = `${t('extendedTextCode.lastSelectedCode', { selectedCode })} `;
                        // Clear after 1 second so it can be re-announced later
                        if (this.liveRegionClearTimeoutId) {
                            clearTimeout(this.liveRegionClearTimeoutId);
                        }

                        this.liveRegionClearTimeoutId = setTimeout(() => {
                            if (liveRegion) {
                                liveRegion.innerText = '';
                            }
                            this.liveRegionClearTimeoutId = null;
                        }, liveRegionClearDelayMs);
                    }
                }
                return false; // Tells Ace to proceed with the arrow key default behavior
            },
            readOnly: true,
        });

        this.props.handleInteractionReady(true);
    };


    getAceEditorStartUpHtml = () => {
        const instanceName = this.state.editorInstanceName;
        return `<div class="ace-editor-container" id="${instanceName}"/>`;
    };

    resetDirty = () => {
        this.editor.getSession().getUndoManager().markClean();
    };

    // Function used in ExtendedTextInteraction
    isDirty = () => {
        if (!this.isEditorInitialized()) {
            return false;
        }
        const editorIsDirty = !this.editor.getSession().getUndoManager().isClean();
        const contentIsDirty = (this.getEditorData() !== this.props.userData);

        return editorIsDirty && contentIsDirty;
    };

    // Function used in ExtendedTextInteraction
    getEditorData = () => {
        const { userData } = this.props;
        if (this.isEditorInitialized()) {
            return this.editor.getValue();
        }
        // if editor is not initialized, return userData from state
        return userData;
    };

    isEditorDataEmpty = () => {
        return this.getEditorData().length === 0;
    };

    resetEditor = () => {
        const { questionModel } = this.props;
        if (this.isEditorInitialized()) {
            try {
                this.editor.setValue('', 1);
            } catch (e) {
                reportError(`Error resetting ACE Editor. ${questionModel.contentRevisionId}`);
            }
        }
    }

    isEditorInitialized = () => {
        return this.editor
            && this.editor.getValue instanceof Function;
    };

    handleCallback = (response, checkAgainstEvaluationTestCases) => {
        if (response.status_code <= 8) {
            this.showCodeCompilationStillInProgress();
        } else if (response.status_code === 11) {
            this.showCodeCompilationError(response);
        } else if ([12, 13, 14, 15].includes(response.status_code)) { // Wrong answer or success
            if (checkAgainstEvaluationTestCases) {
                this.showEvalCodeCompilerResults(response);
            } else {
                this.showSampleCodeCompilaterResults(response);
            }
        } else if (response.errorType === 'HTTP_ERROR') {
            this.showHTTPError(response);
        } else { // 17,20,21 > Finished with errors
            this.showError(response);
        }
    };

    showHTTPError = (response) => {
        this.setState({
            mode: 'httpError',
            error: {
                statusCode: response.response.status,
                statusText: response.response.statusText,
            },
        });
    };

    showError = (response) => {
        this.setState({
            mode: 'error',
            error: {
                statusText: response.status,
            },
        });
    };

    showSampleCodeCompilaterResults = (response) => {
        this.setState({
            mode: 'results',
            sampleTestCasesResults: response.testcases,
        });
    };

    showEvalCodeCompilerResults = (response) => {
        this.setState({
            mode: 'results_eval',
            evalTestCasesResults: response.testcases,
        });
    };

    showCodeCompilationError = (response) => {
        this.setState({
            mode: 'compilationError',
            runTimeInfo: response.runtime_info,
        });
    };

    showCodeCompilationStillInProgress = () => {
        this.setState({
            mode: 'compiling',
        });
    };

    handleCodeCompilerClick = (event) => {
        const { questionModel } = this.props;

        const userData = this.getEditorData();
        const checkAgainstEvaluationTestCases = event.target.id === 'evaluateCode';

        if (userData.length === 0) {
            alert(t('codeCompiler.editorEmpty'));
            return;
        }

        CodeCompilerService.submitCodeForCompilation(questionModel.contentRevisionId, userData, checkAgainstEvaluationTestCases)
            .then((response) => {
                this.handleCallback(response, checkAgainstEvaluationTestCases);
            })
            .catch((error) => {
                this.handleCallback(error);
            });


        this.setState({
            showCodeCompilerModal: true,
            mode: 'compiling',
        });
    };

    closeModal = () => {
        CodeCompilerService.clearCheckStatusInterval();
        this.setState({
            showCodeCompilerModal: false,
        });
    };

    /**
     * Set reference to the run test button for focus management
     * Only sets the reference when accessibility features are enabled
     * @param {HTMLElement} button - The button element reference
     */
    setRunTestButtonRef = (button) => {
        if (extendedTextCodeA11ySelector.getKeyboardInstructions()) {
            this.runTestButton = button;
        }
    };

    /**
     * Handle code submission via Tab key (accessibility feature)
     */
    handleCodeSubmission = () => {
        // Save the current code and announce submission
        this.props.autoSave();

        // After submission, focus should move to the next logical element (run button if available)
        if (extendedTextCodeA11ySelector.getKeyboardInstructions()) {
            this.focusNext();
        }
    };

    render() {
        const { compilationMode, questionModel } = this.props;
        const isViewMediaPrint = sessionReader.isViewMediaPrint();
        const key = `key_${this.state.editorInstanceName}`;
        const evalTestCases = this.state.evalTestCases;
        const evalTestCasesResults = this.state.evalTestCasesResults;
        const globalCompilationError = this.state.globalCompilationError;
        const numberCorrect = evalTestCasesResults.filter(result => result.status_code === 'AC').length;
        const shouldShowResults = sessionReader.isContextViewDelivery() || sessionReader.isContextViewGradingQuestionDelivery();

        // Get accessibility attributes
        const codeQuestionGroupA11yAttributes = extendedTextCodeA11ySelector.getCodeQuestionGroupA11yAttributes(this.props.model, questionModel);
        const codeEditorA11yAttributes = extendedTextCodeA11ySelector.getCodeEditorA11yAttributes();
        const testOutputRegionA11yAttributes = extendedTextCodeA11ySelector.getTestOutputRegionA11yAttributes();
        const runTestButtonA11yAttributes = extendedTextCodeA11ySelector.getRunTestButtonA11yAttributes(this.state.outputRegionId);

        return (
            <div
                {...codeQuestionGroupA11yAttributes}
            >
                { compilationMode ?
                    <ExtendedTextCodeCompilerSamples
                        mode="samples"
                        testCases={this.state.sampleTestCases}
                    />
                    : null
                }
                {this.isA11yV5AndAboveSupported && <div
                    id={`${this.state.editorInstanceName}_live_region`}
                    aria-live="assertive"
                    aria-atomic="true"
                    style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: '0',
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        border: '0',
                    }}
                />}
                <div
                    key={key}
                    ref={(wrapperElement) => {
                        this.aceEditorWrapper = wrapperElement;
                    }}
                    className={`${isViewMediaPrint ? styles.codeWrapper_print : styles.codeWrapper}`}
                    {...codeEditorA11yAttributes}
                    {...(this.state.outputRegionId && { 'aria-controls': this.state.outputRegionId })}
                    dangerouslySetInnerHTML={{ __html: this.getAceEditorStartUpHtml() }}
                />
                {
                    compilationMode && !shouldShowResults ?
                        <button
                            ref={this.setRunTestButtonRef}
                            className={`${styles.compileBtn} btn`}
                            onClick={this.handleCodeCompilerClick}
                            {...runTestButtonA11yAttributes}
                        >
                            {t('codeCompiler.testCode')}
                        </button>
                        : null
                }
                {
                    (
                        evalTestCasesResults.length > 0
                        && (shouldShowResults || isViewMediaPrint)
                    ) ?
                        <div
                            {...(this.state.outputRegionId && { id: this.state.outputRegionId })}
                            {...testOutputRegionA11yAttributes}
                        >
                            <h3 className={styles.responseHeader}>{t('codeCompiler.studentResponseHeader')}</h3>
                            <p>{t('codeCompiler.studentResponseCorrectAnswers', {
                                correct: numberCorrect,
                                total: evalTestCases.length,
                            })}</p>
                            <ExtendedTextCodeCompilerSamples
                                mode="results_eval"
                                testCases={evalTestCases}
                                testCasesResults={evalTestCasesResults}
                            />
                        </div>
                        : null
                }
                {
                    (
                        compilationMode &&
                        globalCompilationError
                        && (shouldShowResults || isViewMediaPrint)
                    ) ?
                        <div
                            {...(this.state.outputRegionId && { id: this.state.outputRegionId })}
                            {...testOutputRegionA11yAttributes}
                        >
                            <h3 className={styles.responseHeader}>{t('codeCompiler.studentResponseHeader')}</h3>
                            <p>{t('codeCompiler.studentResponseCorrectAnswers', {
                                correct: numberCorrect,
                                total: evalTestCases.length,
                            })}</p>
                            <p>
                                <OutcomeIcon isAttempted isCorrect={false} />
                                {t('codeCompiler.globalCompilationError')}
                            </p>
                            <div className={styles.globalCompilationError}>
                                {globalCompilationError}
                            </div>
                        </div>
                        : null
                }
                {
                    compilationMode && this.state.showCodeCompilerModal ?
                        <ExtendedTextCodeCompilerModal
                            mode={this.state.mode}
                            error={this.state.error}
                            runTimeInfo={this.state.runTimeInfo}
                            sampleTestCases={this.state.sampleTestCases}
                            sampleTestCasesResults={this.state.sampleTestCasesResults}
                            evalTestCases={evalTestCases}
                            evalTestCasesResults={evalTestCasesResults}
                            codeCompilerClickHandler={this.handleCodeCompilerClick}
                            closeHandler={this.closeModal}
                        />
                        : null
                }
            </div>
        );
    }
}

export default ExtendedTextCodeInteraction;
